import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguagesByCode,
} from '@eluve/language-utils';

import { parseTranslateResponse } from './parseTranslateResponse';

export const Translator: React.FC<{
  items: string[];
  onTranslated: (items: string[] | null) => void;
  disabled?: boolean;
}> = ({ items, onTranslated, disabled = false }) => {
  const toast = useToast();
  const apiClient = useApiClient();
  const [outputLanguageCode, setOutputLanguageCode] =
    useState(DEFAULT_LANGUAGE);
  const [isTranslating, setIsTranslating] = useState(false);

  const hasItems = items.length > 0;

  const translate = async () => {
    try {
      setIsTranslating(true);
      const response = await apiClient.translation.translate({
        body: {
          outputLanguage: SupportedLanguagesByCode[outputLanguageCode]!,
          items,
        },
      });
      const result = parseTranslateResponse(response, items.length);
      if ('error' in result) {
        toast.error(result.error);
        onTranslated(null);
        return;
      }
      onTranslated(result.items);
    } catch (e) {
      toast.error(`Translation failed. ${(e as Error)?.message}`);
      onTranslated(null);
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <Stack gap={2} direction="horizontal">
      <Combobox>
        <ComboboxSelectButton className="w-full">
          <Text>
            {SupportedLanguagesByCode[outputLanguageCode] ?? 'Select Language'}
          </Text>
        </ComboboxSelectButton>
        <ComboboxDropdown
          className="min-w-52"
          searchPlaceholder="Search languages"
        >
          {Object.keys(SupportedLanguagesByCode).map((languageCode) => (
            <ComboboxOption
              key={languageCode}
              onSelect={async () => {
                setOutputLanguageCode(languageCode);
              }}
            >
              <ComboboxSelectCheck
                className="mr-2"
                selected={languageCode === outputLanguageCode}
              />
              {SupportedLanguagesByCode[languageCode]}
            </ComboboxOption>
          ))}
        </ComboboxDropdown>
      </Combobox>
      <Button
        variant="outline"
        size="sm"
        disabled={isTranslating || disabled || !hasItems}
        onClick={translate}
      >
        {isTranslating ? 'Translating...' : 'Translate'}
      </Button>
    </Stack>
  );
};
